import { Calculator, Check, Scale } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useOpenedPackCalculator } from "./use-opened-pack-calculator";

interface OpenedPackCalculatorPopoverProps {
  itemName: string;
  uomName: string;
  initialActualQty: number;
  initialPackSize?: number | null;
  onApply: (totalQty: number, breakdownNote: string) => void;
  disabled?: boolean;
}

export function OpenedPackCalculatorPopover({
  itemName,
  uomName,
  initialActualQty,
  initialPackSize,
  onApply,
  disabled = false,
}: OpenedPackCalculatorPopoverProps) {
  const calc = useOpenedPackCalculator({
    uomName,
    initialActualQty,
    initialPackSize,
    onApply,
  });

  return (
    <Popover open={calc.isOpen} onOpenChange={calc.setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          title="Kalkulator Kemasan Terbuka / Timbangan"
          className="h-10 w-10 sm:h-8 sm:w-8 shrink-0 text-muted-foreground hover:text-primary hover:border-primary/50"
        >
          <Calculator className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[calc(100vw-2rem)] sm:w-80 max-w-sm p-3.5 shadow-xl border bg-card text-card-foreground"
      >
        <div className="space-y-3">
          {/* Header */}
          <div className="border-b pb-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-foreground truncate max-w-[200px]">
                {itemName}
              </span>
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                {uomName}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Hitung pack utuh + sisa terbuka tanpa kalkulator manual.
            </p>
          </div>

          {/* Mode Tabs */}
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 text-xs">
            <button
              type="button"
              onClick={() => calc.setMode("weight_pieces")}
              className={cn(
                "rounded-md py-1 font-medium transition-all text-[11px] cursor-pointer",
                calc.mode === "weight_pieces"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Scale className="inline-block h-3 w-3 mr-1 -mt-0.5" />
              Timbang / Eceran
            </button>
            <button
              type="button"
              onClick={() => calc.setMode("fraction_volume")}
              className={cn(
                "rounded-md py-1 font-medium transition-all text-[11px] cursor-pointer",
                calc.mode === "fraction_volume"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Desimal / Botol
            </button>
          </div>

          {/* Mode 1: Weight & Pieces (Biji Kopi, Powder, Cup, dsb) */}
          {calc.mode === "weight_pieces" && (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">
                    Kemasan Utuh (Pack/Bag)
                  </Label>
                  <NumberInput
                    decimals={4}
                    value={calc.sealedQty}
                    onChange={calc.setSealedQty}
                    className="h-8 text-right font-mono text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">
                    Isi per Kemasan
                  </Label>
                  <NumberInput
                    decimals={4}
                    value={calc.packSize}
                    onChange={calc.setPackSize}
                    className="h-8 text-right font-mono text-xs"
                  />
                </div>
              </div>

              {/* Preset Chips */}
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[10px] text-muted-foreground">
                  Preset isi:
                </span>
                {[
                  { label: "1.000 (1kg)", val: 1000 },
                  { label: "750 (Sirup)", val: 750 },
                  { label: "500", val: 500 },
                  { label: "50 (Slop)", val: 50 },
                ].map((preset) => (
                  <button
                    key={preset.val}
                    type="button"
                    onClick={() => calc.setPackSizePreset(preset.val)}
                    className={cn(
                      "rounded border px-1.5 py-0.5 text-[10px] font-mono transition-colors cursor-pointer",
                      calc.packSize === preset.val
                        ? "border-primary bg-primary/10 text-primary font-semibold"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Loose / Weighed remainder */}
              <div className="space-y-1 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] text-muted-foreground">
                    Sisa Terbuka / Timbangan Bar
                  </Label>
                  <span className="text-[10px] text-muted-foreground">
                    Satuan: {uomName}
                  </span>
                </div>
                <NumberInput
                  decimals={4}
                  value={calc.looseQty}
                  onChange={calc.setLooseQty}
                  placeholder="Contoh: 380"
                  className="h-8 text-right font-mono text-xs font-semibold"
                />
              </div>
            </div>
          )}

          {/* Mode 2: Fraction & Liquid Level (Botol Sirup ber-pump, Susu) */}
          {calc.mode === "fraction_volume" && (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">
                    Botol Utuh Segel
                  </Label>
                  <NumberInput
                    decimals={4}
                    value={calc.sealedQty}
                    onChange={calc.setSealedQty}
                    className="h-8 text-right font-mono text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">
                    Isi per Botol ({uomName})
                  </Label>
                  <NumberInput
                    decimals={4}
                    value={calc.packSize}
                    onChange={calc.setPackSize}
                    className="h-8 text-right font-mono text-xs"
                  />
                </div>
              </div>

              {/* Fraction chips for partial bottle */}
              <div className="space-y-1 pt-1">
                <Label className="text-[11px] text-muted-foreground">
                  Sisa Terbuka (Pecahan / Skala Botol)
                </Label>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { label: "0 (Habis)", val: 0 },
                    { label: "0.25 (¼)", val: 0.25 },
                    { label: "0.5 (½)", val: 0.5 },
                    { label: "0.75 (¾)", val: 0.75 },
                  ].map((f) => (
                    <button
                      key={f.val}
                      type="button"
                      onClick={() => calc.setFractionPreset(f.val)}
                      className={cn(
                        "rounded border py-1 text-center font-mono text-[10px] transition-colors cursor-pointer",
                        calc.fraction === f.val
                          ? "border-primary bg-primary/10 text-primary font-bold"
                          : "border-border text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Custom Desimal:</span>
                  <NumberInput
                    decimals={2}
                    value={calc.fraction}
                    onChange={(n) => calc.setFraction(Math.min(1, n))}
                    className="h-6 w-20 text-right font-mono text-[10px]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Calculation Live Result Box */}
          <div className="rounded-md border border-primary/20 bg-primary/5 p-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">
                Total Stok Fisik:
              </span>
              <span className="font-mono text-sm font-extrabold text-primary">
                {calc.calculatedTotal.toLocaleString("id-ID")}{" "}
                <span className="text-xs font-medium text-foreground">
                  {uomName}
                </span>
              </span>
            </div>
            <p className="text-[10px] font-mono text-muted-foreground truncate">
              {calc.summaryFormula}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => calc.setIsOpen(false)}
              className="min-h-[40px] sm:min-h-0 sm:h-7 text-xs"
            >
              Batal
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={calc.handleApply}
              className="min-h-[40px] sm:min-h-0 sm:h-7 text-xs font-semibold"
            >
              <Check className="h-3 w-3 mr-1" />
              Terapkan ke Fisik
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
